'use client';

import React from 'react';
import { useRef } from 'react';
import { useTranslations } from 'next-intl';
import styles from './RingWithHome.module.css';
import { Container } from 'components/shared';
import { CustomImage as Image } from 'components/shared';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
import { SectionHeader } from './SectionHeader';

// Section wrapper
const Section = React.forwardRef<
  HTMLElement,
  React.HTMLAttributes<HTMLElement>
>(({ className, children, ...props }, ref) => (
  <section
    ref={ref}
    className={className ? `${styles.section} ${className}` : styles.section}
    {...props}
  >
    {children}
  </section>
));

Section.displayName = 'Section';

// Banner image container
const BannerContainer = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.bannerContainer}>{children}</div>
);

const BannerImageWrapper = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.bannerImageWrapper}>{children}</div>
);

// Cards grid (structure from UpcomingIntegrations, but with dark theme)
const Grid = ({
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={styles.grid} {...props}>
    {children}
  </div>
);

// Dark-themed card component (new design, not editing original)
const DarkCard = ({
  children,
  ...props
}: React.HTMLAttributes<HTMLElement>) => (
  <article className={styles.darkCard} {...props}>
    {children}
  </article>
);

const CardAsset = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.cardAsset}>{children}</div>
);

const CardImage = ({
  $blendMode,
  ...props
}: {
  $blendMode?: React.CSSProperties['mixBlendMode'];
} & React.ImgHTMLAttributes<HTMLImageElement>) => (
  <img
    alt=""
    className={styles.cardImage}
    style={
      $blendMode
        ? ({ ['--blend-mode']: $blendMode } as React.CSSProperties)
        : undefined
    }
    {...props}
  />
);

const CardText = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.cardText}>{children}</div>
);

const CardTitle = ({ children }: { children: React.ReactNode }) => (
  <h3 className={styles.cardTitle}>{children}</h3>
);

const CardSubtitle = (
  props: React.HTMLAttributes<HTMLParagraphElement>,
) => <p className={styles.cardSubtitle} {...props} />;

type CardContent = {
  titleKey: string;
  descriptionKey: string;
  image: string;
  blendMode?: React.CSSProperties['mixBlendMode'];
};

const cardItems: CardContent[] = [
  {
    titleKey: 'ringWithHome.cards.insideOut.title',
    descriptionKey: 'ringWithHome.cards.insideOut.description',
    image: '/web_v2/uh-home-v2/home-and-ring/updated/1.png',
  },
  {
    titleKey: 'ringWithHome.cards.sleepStages.title',
    descriptionKey: 'ringWithHome.cards.sleepStages.description',
    image: '/web_v2/uh-home-v2/home-and-ring/updated/2.png',
  },
  {
    titleKey: 'ringWithHome.cards.recovery.title',
    descriptionKey: 'ringWithHome.cards.recovery.description',
    image: '/web_v2/uh-home-v2/home-and-ring/updated/3.png',
  },
];

interface RingWithHomeProps {
  className?: string;
  htmlId?: string;
}

const RingWithHome = ({ className, htmlId }: RingWithHomeProps) => {
  const rootRef = useRef<HTMLElement>(null);
  const t = useTranslations('uhHome');

  return (
    <Section
      className={className}
      id={htmlId}
      ref={rootRef}
      role="region"
      aria-labelledby={htmlId ? `${htmlId}-heading` : 'ring-with-home-heading'}
    >
      <Container>
        <BannerContainer>
          <BannerImageWrapper>
            <Image
              src={'web_v2/uh-home-v2/banners/ring-home.png'}
              alt={t('ringWithHome.bannerImage.alt')}
              fill
              style={{ objectPosition: 'center' }}
            />
          </BannerImageWrapper>
        </BannerContainer>

        <SectionHeader
          title={t('ringWithHome.title')}
          subtitle={t('ringWithHome.subtitle')}
          variant="dark"
          titleId={htmlId ? `${htmlId}-heading` : 'ring-with-home-heading'}
          marginBottom="8rem"
          showComingSoon={true}
        />

        <Grid role="list" aria-label={t('ringWithHome.ariaLabel.features')}>
          {cardItems.map((item) => (
            <DarkCard key={item.titleKey} role="listitem">
              <CardAsset>
                <CardImage
                  src={getCompressedAssetUrl(item.image)}
                  alt={t(item.titleKey)}
                  $blendMode={item.blendMode}
                />
              </CardAsset>
              <CardText>
                <CardTitle>{t(item.titleKey)}</CardTitle>
                <CardSubtitle>{t(item.descriptionKey)}</CardSubtitle>
              </CardText>
            </DarkCard>
          ))}
        </Grid>
      </Container>
    </Section>
  );
};

export default RingWithHome;
